import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import IssueGuide from '../IssueGuide';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../services/_common';
import SkeletonTable from '../SkeletonTable';
import Carousel from 'react-multi-carousel';
import { CustomDot } from './Carousel';
import { useEffect, useMemo, useRef, useState } from 'react';
import TextEditor from '../TextEditor';
import { IUser } from '../../atoms';

const Root = styled.div`
  user-select: none;
  height: 100%;

  &.small {
    > .widget-headers {
      display: none;
    }

    > .widget-body {
      &.card {
        padding: 0.95rem;
        .notice-box {
          border-radius: 1.5rem;
          .notice-header {
            .notice-info span {
              font-size: 0.875rem;
            }
          }
        }
      }
    }
  }

  > .widget-headers {
    width: 100%;
    padding: 0.75rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex-grow: 0;

    &.margin-top {
      margin-bottom: 0;
    }
    > .widget-title {
      padding: 0 1rem;
      align-items: center;
    }
  }

  > .widget-body {
    &.card {
      width: 100%;
      height: calc(100% - 3.5rem);
      padding: 0 0.95rem 0.95rem;
      flex-grow: 1;

      .react-multi-carousel-list,
      .react-multi-carousel-track {
        height: 100%;
      }

      .notice-box {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        width: 100%;
        height: 100%;
        border: 1px solid var(--outline);
        border-radius: 0.5rem;
        padding: 1rem;

        .notice-header {
          display: flex;
          flex-direction: row;
          gap: 0.1rem;
          justify-content: space-between;

          .notice-info {
            color: var(--widget-text-secondary);

            span {
              font-size: var(--widget-font-size-base);
              line-height: 1.2;
            }
          }
          h4 {
            color: var(--text-primary);
            font-size: var(--widget-font-size-lg);
            line-height: 1.2;
            white-space: break-spaces;
            font-weight: 700;
          }
        }

        /* ckeditor custom style */
        .ck-editor-cont {
          height: 100%;
          overflow: hidden;
          border-top: 1px solid var(--outline);

          .ckeditor {
            height: 100%;

            /* 에디터 상단 툴바 제거 */
            .ck-editor__top {
              display: none;
            }

            .ck-rounded-corners .ck.ck-editor__main > .ck-editor__editable,
            .ck.ck-editor__main > .ck-editor__editable.ck-rounded-corners {
              height: 100%;
              border: none;
              padding: 0;
              overflow: hidden;
              white-space: normal;
            }
          }
          .ck-content span {
            white-space: normal !important;
            word-break: break-word !important;
            overflow-wrap: break-word !important;
            word-wrap: break-word !important;
          }
        }
      }

      .no-data {
        width: 100%;
        height: 100%;
        border: 1px solid var(--outline);
        border-radius: 0.5rem;
      }
    }
  }

  @keyframes slide {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(calc(-100% + 199px));
    }
  }
`;

const ANIMATION_DELAY = 3; // 애니메이션 딜레이 시간 / 3 = 3초;

export const SiteNotice = ({ name, userInfo, refetchInterval, size = 'small' }: { name: string; userInfo: IUser; refetchInterval: number; size: 'large' | 'small' }) => {
  const { hCd, sCd } = userInfo;
  const { t } = useTranslation();
  const [curIdx, setCurIdx] = useState(0); // carousel 현재 인덱스 - carousel 슬라이드 변경시 애니메이션 초기화를 위해 사용
  const ckRefs = useRef<(HTMLDivElement | null)[]>([]);
  const carouselRef = useRef<any>(null); // carousel ref 추가

  const {
    data: siteNoteBoardData,
    isLoading,
    isError,
  } = useQuery(['siteNoteBoardGet', userInfo], () => fetchData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(refetchInterval !== undefined && { refetchInterval }),
  });

  const fetchData = async () => {
    try {
      const res = await apiGet({ path: '/safe/communicatecontents', req: { hCd, sCd } });
      if (res.status === 200) return res.data.data;
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  useEffect(() => {
    let targetElement: HTMLDivElement;
    let animationCompleted = false;

    const checkElement = () => {
      targetElement = document.querySelector('.urgent-wrap .react-multi-carousel-item--active .ckeditor') as HTMLDivElement;

      if (targetElement) {
        const viewHeight = Math.floor(document.querySelector('.urgent-wrap .react-multi-carousel-item--active .ck-editor-cont')?.getBoundingClientRect().height || 0);
        const contentHeight = targetElement?.scrollHeight || 0;

        // 내용이 뷰보다 클 때만 애니메이션 적용
        if (contentHeight > viewHeight - 8) {
          const fmtSecond = (contentHeight * 0.05).toFixed(2);
          const animationDuration = parseFloat(fmtSecond);

          // CSS 애니메이션 적용
          targetElement.style.animation = `slide ${fmtSecond}s ${ANIMATION_DELAY}s linear`;

          // 애니메이션 완료 후 다음 슬라이드로 이동
          const totalTime = (ANIMATION_DELAY + animationDuration + 2) * 1000; // 애니메이션 시간 + 2초 대기

          setTimeout(() => {
            if (carouselRef.current && siteNoteBoardData?.communicateList?.length > 1 && !animationCompleted) {
              animationCompleted = true;
              carouselRef.current.next();
            }
          }, totalTime);
        } else {
          // 내용이 짧을 때는 5초 후 다음 슬라이드로 이동
          setTimeout(() => {
            if (carouselRef.current && siteNoteBoardData?.communicateList?.length > 1 && !animationCompleted) {
              animationCompleted = true;
              carouselRef.current.next();
            }
          }, 5000);
        }
      } else {
        // 요소를 찾지 못했을 때 요소 탐색 재시도
        setTimeout(checkElement, 100);
      }
    };

    checkElement();

    return () => {
      // cleanup 함수
    };
  }, [curIdx, siteNoteBoardData?.communicateList?.length]);

  const responsive = useMemo(
    () => ({
      desktop: {
        breakpoint: { max: 3000, min: 1024 },
        items: 1,
        // items: size === 'large' ? carouselCount : 1,
        slidesToSlide: 1, // optional, default to 1.
      },
      tablet: {
        breakpoint: { max: 1024, min: 464 },
        items: 1,
        // items: size === 'large' ? 2 : 1,
        slidesToSlide: 1, // optional, default to 1.
      },
      mobile: {
        breakpoint: { max: 464, min: 0 },
        items: 1,
        slidesToSlide: 1, // optional, default to 1.
      },
    }),
    []
  );

  const isSwiping = siteNoteBoardData && siteNoteBoardData?.communicateList?.length > 1;
  const loading = !siteNoteBoardData?.communicateList || isLoading;
  if (isError) return <IssueGuide />;

  return (
    <Root className={`urgent-wrap ${size}`}>
      <div className='widget-headers'>
        <div className='widget-title flex-between'>
          <span>{t(name)}</span>
        </div>
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable rows={2} />
        </div>
      ) : (
        <div className='widget-body card'>
          {siteNoteBoardData?.communicateList?.length > 0 ? (
            <Carousel
              ref={carouselRef}
              swipeable={isSwiping}
              draggable={isSwiping}
              responsive={responsive}
              infinite={isSwiping}
              autoPlay={false} // 자동 재생 비활성화 (애니메이션 완료 후 수동으로 넘기기)
              keyBoardControl
              transitionDuration={500}
              containerClass='carousel-container'
              removeArrowOnDeviceType={['tablet', 'mobile', 'desktop']}
              dotListClass='custom-dot-list-style'
              showDots={isSwiping}
              customDot={<CustomDot mergedData={siteNoteBoardData?.communicateList} />}
              afterChange={(previousSlide, { currentSlide, onMove }: any) => {
                setCurIdx(currentSlide);

                // Carousel 슬라이드 변경시 애니메이션 초기화
                const targetAll: NodeListOf<HTMLDivElement> | null = document.querySelectorAll('.urgent-wrap .react-multi-carousel-item');

                if (targetAll) {
                  targetAll.forEach((nodeEl: HTMLDivElement) => {
                    const childNode = nodeEl.querySelector('.ckeditor') as HTMLDivElement | null;
                    if (childNode) {
                      childNode.style.animation = 'none';
                    }
                  });
                }
              }}
            >
              {siteNoteBoardData?.communicateList?.map((el: any, index: number) => (
                <div key={el.cSeq} className='notice-box'>
                  <div className='notice-header'>
                    <h4>{el.cTitle}</h4>
                    <div className='notice-info'>
                      <span>{el.cDate}</span>
                    </div>
                  </div>

                  <div className='ck-editor-cont'>
                    <div
                      ref={(item: any) => {
                        ckRefs.current[index] = item;
                      }}
                    >
                      <TextEditor content={el.cContent} readOnly idx={index} />
                    </div>
                  </div>
                </div>
              ))}
            </Carousel>
          ) : (
            <div className='no-data'>
              <IssueGuide text='현장 전달사항이 없습니다' isImg={false} />
            </div>
          )}
        </div>
      )}
    </Root>
  );
};
